import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Endpoint } from '@enums/endpoint.enum';
import { environment } from '@env/environment';
import { AuthService } from '@services/auth.service';
import { UserService } from '@services/user.service';
import { InstanceService } from '@services/instance.service';
import { firstValueFrom, from, Observable, switchMap, take, throwError, catchError, tap, finalize } from 'rxjs';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  private refreshPromise: Promise<boolean> | null = null;
  private isSyncingInstance = false;

  constructor(
    private router: Router,
    private auth: AuthService,
    private userService: UserService,
    private instanceService: InstanceService,
  ) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    if (this.shouldSkip(req)) {
      return next.handle(req);
    }

    const token = this.auth.getAccessToken();

    if (!token?.jwt) {
      return next.handle(req);
    }

    if (token.isExpired()) {
      this.logoutLocally();

      return throwError(() => new Error('Token expired'));
    }

    if (token.isExpireSoon()) {

      if (this.refreshPromise !== null) {
        return from(this.refreshPromise).pipe(
          switchMap((success) => {
            if (!success) {
              return throwError(() => new Error('Token refresh failed'));
            }

            const newToken = this.auth.getAccessToken();
            if (!newToken?.jwt) {
              return throwError(() => new Error('Token missing after refresh'));
            }

            const authReq = this.addAuthHeader(req);

            return next.handle(authReq);
          })
        );
      }

      this.refreshPromise = firstValueFrom(this.auth.refreshToken())
        .catch(() => {
          return false;
        })
        .then((success: boolean) => {
          if (!success) {
            this.logoutLocally();
          }

          return success;
        })
        .finally(() => {
          this.refreshPromise = null;
        });

      return from(this.refreshPromise).pipe(
        switchMap((success) => {
          if (!success) {
            return throwError(() => new Error('Token refresh failed'));
          }

          const newToken = this.auth.getAccessToken();
          if (!newToken || !newToken.jwt) {
            return throwError(() => new Error('Token missing after refresh'));
          }

          const authReq = this.addAuthHeader(req);

          return next.handle(authReq);
        })
      );
    }

    const authReq = this.addAuthHeader(req);

    return next.handle(authReq).pipe(
      tap(event => this.syncInstance(req, event)),
      catchError((error) => {
        if (error.status === 401) {
          this.logoutLocally();
        }

        return throwError(() => error);
      })
    );
  }

  private syncInstance(req: HttpRequest<any>, event: HttpEvent<any>): void {
    if (!(event instanceof HttpResponse) || req.url.includes(Endpoint.ME) || this.isSyncingInstance) {
      return;
    }

    const instanceId = Number(event.headers.get('X-Instance-Id'));
    const me = this.userService.me();

    if (!instanceId || !me || me.instanceId === instanceId) {
      return;
    }

    this.isSyncingInstance = true;
    this.userService.getMe().pipe(
      finalize(() => this.isSyncingInstance = false),
    ).subscribe({ error: () => {} });
  }

  private logoutLocally(): void {
    this.auth.removeAccessToken();
    this.userService.clear();
    this.instanceService.clear();
    this.router.navigate(['/auth/login']);
  }

  private addAuthHeader(req: HttpRequest<any>): HttpRequest<any> {

    const token = this.auth.getAccessToken();

    return req.clone({
      setHeaders: {
        Authorization: `Bearer ${token.jwt}`,
      },
    });
  }

  private shouldSkip(req: HttpRequest<any>): boolean {
    if (!req.url.startsWith(environment.apiBaseUrl)) {
      return true;
    }

    const skipRoutes = [Endpoint.TOKEN, Endpoint.REFRESH_TOKEN];

    return skipRoutes.some(path => req.url.includes(path));
  }
}
